import 'satellite-polyfills';
import '@/lib/pwaBeforeInstallPrompt.ts';

import { registerSW } from 'virtual:pwa-register';
import { createApp } from 'vue';
import { plugin as featureFlagPlugin } from '@loadsmart/vue-feature-flags';
import featureFlagConfig from '@/plugins/featureFlagPlugin';
import App from './App.vue';
import { createCirceRouter } from './router';
import { createPinia } from 'pinia';

import vuetify from './plugins/vuetifyPlugin.ts';
import AxiosPlugin from '@satellite/plugins/AxiosPlugin';
import SocketPlugin from '@satellite/plugins/SocketPlugin';
import { HTTP_CLIENT_KEY } from '@satellite/composables/useHttpClient';
import { analyticsKey } from '@satellite/composables/useAnalytics';
import { installVueQuery } from '@/plugins/vueQuery';
import Notifications from '@kyvg/vue3-notification';
import type { NamedValue } from 'vue-i18n';

import i18n from './plugins/i18n';
import { setTranslator as setCoreTranslator, uiAppName } from '@nova/core';
import { setTranslator } from '@satellite/plugins/translator';

import { defaultNotify } from '@/lib/notify.ts';
import type { IMeResponse } from '@/types/apiTypes';
import { createCirceAnalyticsClient } from '@/analytics/client';
import { useCirceAnalytics } from '@/composables/useCirceAnalytics';

import AuthService from '@satellite/services/auth-service';
import useEventHub from './composables/useEventHub.ts';

registerSW({ immediate: true });

type Translator = (key: string, ...args: unknown[]) => string;
setTranslator(i18n.global.t as unknown as Translator, i18n.global.locale);
setCoreTranslator((key, params?: NamedValue) =>
  (i18n.global.t as unknown as Translator)(key, params)
);

const initApp = async () => {
  const app = createApp(App);
  const pinia = createPinia();
  const eventHub = useEventHub();

  const queryClient = installVueQuery(app);
  app.use(Notifications);
  app.use(i18n);
  app.use(vuetify);
  app.use(pinia);
  app.use(featureFlagPlugin as any, { config: featureFlagConfig });

  const { useAuthStore } = await import('@/store/useAuthStore.ts');
  const authStore = useAuthStore();
  const { logout, setUser } = authStore;

  AxiosPlugin.init('/login', uiAppName.CIRCE, app, { logout });
  app.provide(HTTP_CLIENT_KEY, globalThis.axios);
  const analyticsClient = createCirceAnalyticsClient();
  app.provide(analyticsKey, analyticsClient);
  app.runWithContext(() => {
    useCirceAnalytics();
  });

  // The router's afterEach hook drives the Session Replay gate, so it needs the client.
  const router = createCirceRouter(analyticsClient);

  let fetchedUser;

  const token = localStorage.getItem('access_token');
  if (token) {
    try {
      AxiosPlugin.setAccessToken(token);
      const { authKeys } = await import('@/composables/queryKeys');

      const user = await queryClient.fetchQuery({
        queryKey: authKeys.me(),
        queryFn: AuthService.getMe,
        staleTime: 1000 * 60 * 5
      });

      if (user) {
        setUser(user as IMeResponse);
        fetchedUser = user;
      }
    } catch {
      logout();
      await router.replace({ name: 'login' });
    }
  }

  app.use(router);
  app.config.globalProperties.notify = defaultNotify;
  app.config.globalProperties.$globalLoading = false;

  // TODO(william): migrate this to a proper store instead of manually constructing
  // the store object to match SocketPlugin's interface.
  app.config.globalProperties.$store = {
    state: {
      Auth: { me: fetchedUser }
    },
    modules: () => ({})
  };
  app.config.globalProperties.sockets = new SocketPlugin(app);
  app.config.globalProperties.$eventHub = eventHub;

  const socketPlugin: SocketPlugin = app.config.globalProperties.sockets;
  if (token) {
    socketPlugin.connect(token);
  }

  app.mount('#app');
};

initApp();
